/**
 * C2-5 (#1314) — time on site, on the job.
 *
 * ONE number, with ONE definition, read from ONE place:
 * `job_performance.on_site_minutes` — **Start Work → complete**, computed once
 * on completion (`docker/init/217_job_performance.sql`) and read here. It is
 * deliberately not recomputed from the `work_started_at` / `completed_at`
 * stamps sitting beside it in the same payload; a second implementation of the
 * same number is the D2 trap, and the two would drift the moment either
 * definition moved.
 *
 * Definitions, and the rules every labour surface follows:
 * `docs/bible/labour-numbers.md`.
 *
 * WHY IT SHOWS A DASH. `on_site_minutes` is NULL when the tech never tapped
 * Start Work. A zero would be a claim that the job took no time; a dash is the
 * truth, which is "we don't know". This is the C1-11 lesson — Insights showed
 * "Avg Completion 255.5h" for months because a number nobody had defined was
 * rendered as though it meant something.
 */
import { Clock } from "lucide-react";

/** Minutes → the shortest honest phrasing. Matches Insights' "Avg Time on
 *  Site" so the same quantity reads the same way on both screens. */
export function formatOnSite(minutes: number | null | undefined): string | null {
    if (minutes === null || minutes === undefined || !Number.isFinite(minutes)) return null;
    if (minutes < 0) return null;
    if (minutes < 60) return `${Math.round(minutes)} min`;
    return `${(minutes / 60).toFixed(1)}h`;
}

/**
 * Why a job has no time on site. THREE cases, not two — the proof run at 390px
 * caught the missing one: a job that is merely *scheduled* was being told
 * "Start Work was never tapped", which reads as somebody's omission on a job
 * nobody has done yet. Only a job that finished without the tap is an omission.
 */
/** Statuses where the visit is over — "not finished yet" would be false. */
const FINISHED_STATUSES = new Set(["office_review", "completed", "closed"]);

export function unknownReason(
    workStartedAt: string | null | undefined,
    completedAt: string | null | undefined,
    status?: string | null,
): string {
    // A finished job with a start stamp but no total: the wrap time is what's
    // missing (audit 2026-10-01, #082664668 said "not finished yet").
    if (workStartedAt && (completedAt || (status && FINISHED_STATUSES.has(status)))) {
        return "finish time not recorded";
    }
    if (workStartedAt) return "not finished yet";
    if (completedAt) return "Start Work was never tapped";
    return "hasn't started yet";
}

export function TimeOnSite({
    onSiteMinutes,
    workStartedAt,
    completedAt,
    status,
}: {
    onSiteMinutes: number | null | undefined;
    workStartedAt: string | null | undefined;
    completedAt?: string | null | undefined;
    status?: string | null;
}) {
    const value = formatOnSite(onSiteMinutes);
    const why = unknownReason(workStartedAt, completedAt, status);

    return (
        <span
            className="inline-flex items-center gap-1.5 text-xs text-foreground/70"
            title="Start Work → complete. See docs/bible/labour-numbers.md"
        >
            <Clock className="h-3.5 w-3.5 shrink-0" />
            <span>
                Time on site:{" "}
                {value ? (
                    <strong className="text-foreground/90 tabular-nums">{value}</strong>
                ) : (
                    <>
                        <span className="text-foreground/60">—</span>{" "}
                        <span className="text-foreground/60">({why})</span>
                    </>
                )}
            </span>
        </span>
    );
}
